/* src/world/ambient.ts — the city has people in it.
 *
 * Cars run the roads and pedestrians walk the footpaths and sidewalks, driven by the same road
 * centrelines the tile ribbons were built from (the geometry worker emits them alongside the
 * meshes). This is ambience, not simulation the server needs to know about: nothing here touches
 * money or state, it is the city looking inhabited — including through a shop's windows, where
 * the frame loop keeps driving it while the player is indoors.
 *
 * Cost discipline: every car is one instance of one merged low-poly geometry, every pedestrian
 * two (body, head — separate meshes so shirts can tint per-instance while heads stay skin).
 * Three draw calls and a few thousand triangles for the whole population, updated with one
 * matrix write per agent per frame. Nothing allocates on the frame path.
 */

import {
  Color,
  DynamicDrawUsage,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Object3D,
  type Scene,
} from 'three';
import {
  carGeometry,
  personBodyGeometry,
  personHeadGeometry,
  SHIRT_COLOURS,
  SKIN,
} from '../render/peopleGeometry.ts';
import type { PackedPaths } from '../workers/protocol.ts';

export interface AmbientSource {
  readonly key: string;
  readonly group: Object3D;
  readonly paths: PackedPaths;
}

export interface AmbientLife {
  /** Advance everyone. `focusX/Z` is the point life should cluster around (camera target). */
  update: (dt: number, focusX: number, focusZ: number, sources: readonly AmbientSource[]) => void;
  dispose: () => void;
}

const MAX_CARS = 110;
const MAX_PEOPLE = 190;
/** Life exists inside this radius of the focus, and is recycled beyond it. */
const LIVE_R = 620;
const SPAWN_R = 520;
/** Per-frame spawn budget — the population fades in over a couple of seconds, never pops. */
const SPAWNS_PER_FRAME = 6;

/** A path prepared for walking: world-local segment table with cumulative lengths. */
interface PreparedPath {
  readonly sourceKey: string;
  readonly group: Object3D;
  /** Tile-local points, straight from the worker. */
  readonly pts: Float32Array;
  readonly first: number;
  readonly count: number;
  readonly code: number;
  readonly halfWidth: number;
  readonly cum: Float32Array;
  readonly length: number;
}

interface Agent {
  path: PreparedPath | null;
  /** Metres along the path. */
  t: number;
  dir: 1 | -1;
  speed: number;
  /** Lateral offset from the centreline, metres (sidewalk side for walkers, lane for cars). */
  lateral: number;
  bobPhase: number;
}

const CAR_SPEED: Readonly<Record<number, number>> = { 1: 6.5, 2: 9, 3: 11, 4: 12.5, 5: 17 };

const CAR_COLOURS = [0xc94f3f, 0x3f6fc9, 0xd8d2c4, 0x33383f, 0x5a8a4a, 0xc9a23f, 0x8a8f96];

export function createAmbientLife(scene: Scene): AmbientLife {
  const carMat = new MeshLambertMaterial({ vertexColors: true });
  const peopleMat = new MeshLambertMaterial({ vertexColors: true });

  const cars = new InstancedMesh(carGeometry(), carMat, MAX_CARS);
  const bodies = new InstancedMesh(personBodyGeometry(), peopleMat, MAX_PEOPLE);
  const heads = new InstancedMesh(personHeadGeometry(), peopleMat, MAX_PEOPLE);
  for (const m of [cars, bodies, heads]) {
    m.instanceMatrix.setUsage(DynamicDrawUsage);
    m.count = 0;
    m.frustumCulled = false;
    m.castShadow = false;
    m.receiveShadow = false;
    scene.add(m);
  }

  const carAgents: Agent[] = [];
  const walkAgents: Agent[] = [];
  const dummy = new Object3D();
  const zero = new Matrix4().makeScale(0, 0, 0);
  const colour = new Color();

  /*
   * Prepared paths, cached PER SOURCE TILE. During a city fill the resident set changes on
   * nearly every frame, and rebuilding every tile's cumulative-length tables each time was a
   * steady main-thread tax exactly when the frame could least afford it (the profiler read
   * ~22 ms of CPU p95 mid-fill). Now a tile's paths are prepared once when it first appears
   * and simply dropped when it goes; only the flat walkable/drivable lists re-concatenate.
   */
  const preparedBySource = new Map<string, PreparedPath[]>();
  let walkable: PreparedPath[] = [];
  let drivable: PreparedPath[] = [];
  let sourcesKey = '';

  function prepareSource(s: AmbientSource): PreparedPath[] {
    const out: PreparedPath[] = [];
    const meta = s.paths.meta;
    const pts = s.paths.points;
    for (let m = 0; m + 3 < meta.length; m += 4) {
      const first = meta[m] ?? 0;
      const count = meta[m + 1] ?? 0;
      const code = meta[m + 2] ?? 1;
      const halfWidth = meta[m + 3] ?? 3;
      if (count < 2) continue;
      const cum = new Float32Array(count);
      let len = 0;
      for (let i = 1; i < count; i += 1) {
        const ax = pts[(first + i - 1) * 2] ?? 0;
        const az = pts[(first + i - 1) * 2 + 1] ?? 0;
        const bx = pts[(first + i) * 2] ?? 0;
        const bz = pts[(first + i) * 2 + 1] ?? 0;
        len += Math.hypot(bx - ax, bz - az);
        cum[i] = len;
      }
      if (len < 12) continue; // too short to be worth an agent
      out.push({ sourceKey: s.key, group: s.group, pts, first, count, code, halfWidth, cum, length: len });
    }
    return out;
  }

  function prepare(sources: readonly AmbientSource[]): void {
    const key = sources.map((s) => s.key).join('|');
    if (key === sourcesKey) return;
    sourcesKey = key;

    const liveKeys = new Set(sources.map((s) => s.key));
    for (const cachedKey of [...preparedBySource.keys()]) {
      if (!liveKeys.has(cachedKey)) preparedBySource.delete(cachedKey);
    }
    for (const s of sources) {
      if (!preparedBySource.has(s.key)) preparedBySource.set(s.key, prepareSource(s));
    }

    walkable = [];
    drivable = [];
    for (const paths of preparedBySource.values()) {
      for (const p of paths) {
        if (p.code <= 4) walkable.push(p);
        if (p.code >= 1) drivable.push(p);
      }
    }
    // Agents whose tile was evicted lose their path and respawn on a live one.
    for (const a of [...carAgents, ...walkAgents]) {
      if (a.path && !liveKeys.has(a.path.sourceKey)) a.path = null;
    }
  }

  /** Position + forward direction at `t` metres along a path, in local world metres. */
  function sample(
    p: PreparedPath, t: number,
    out: { x: number; z: number; dx: number; dz: number },
  ): void {
    const target = Math.max(0, Math.min(p.length, t));
    // Binary search the cumulative table.
    let lo = 1;
    let hi = p.count - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((p.cum[mid] ?? 0) < target) lo = mid + 1;
      else hi = mid;
    }
    const segStart = p.cum[lo - 1] ?? 0;
    const segLen = Math.max(1e-6, (p.cum[lo] ?? 0) - segStart);
    const f = (target - segStart) / segLen;
    const ax = p.pts[(p.first + lo - 1) * 2] ?? 0;
    const az = p.pts[(p.first + lo - 1) * 2 + 1] ?? 0;
    const bx = p.pts[(p.first + lo) * 2] ?? 0;
    const bz = p.pts[(p.first + lo) * 2 + 1] ?? 0;
    const gx = p.group.position.x;
    const gz = p.group.position.z;
    // The tile group carries the origin-scale ratio (~1.0); points are in built metres.
    const gs = p.group.scale.x;
    out.x = gx + (ax + (bx - ax) * f) * gs;
    out.z = gz + (az + (bz - az) * f) * gs;
    const dl = Math.max(1e-6, Math.hypot(bx - ax, bz - az));
    out.dx = (bx - ax) / dl;
    out.dz = (bz - az) / dl;
  }

  function nearFocus(p: PreparedPath, fx: number, fz: number): boolean {
    // Cheap gate: the path's first point within spawn radius. Paths are tile-sized, close enough.
    const x = p.group.position.x + (p.pts[p.first * 2] ?? 0) * p.group.scale.x;
    const z = p.group.position.z + (p.pts[p.first * 2 + 1] ?? 0) * p.group.scale.x;
    return Math.hypot(x - fx, z - fz) < SPAWN_R;
  }

  function respawn(a: Agent, pool: PreparedPath[], fx: number, fz: number, isCar: boolean, rand: () => number): boolean {
    // A handful of random probes beats filtering the whole pool every spawn.
    for (let tries = 0; tries < 8; tries += 1) {
      const p = pool[Math.floor(rand() * pool.length)];
      if (!p || !nearFocus(p, fx, fz)) continue;
      if (isCar && p.code === 0) continue;
      if (!isCar && p.code === 5) continue;
      a.path = p;
      a.t = rand() * p.length;
      a.dir = rand() < 0.5 ? 1 : -1;
      if (isCar) {
        a.speed = (CAR_SPEED[p.code] ?? 8) * (0.85 + rand() * 0.3);
        // Keep left, like the city we ingested: offset to the travel direction's left.
        a.lateral = Math.max(0.95, p.halfWidth * 0.5);
      } else {
        a.speed = 1.15 + rand() * 0.65;
        // Footways: on the way itself. Roads: on the sidewalk beyond the kerb.
        a.lateral = p.code === 0 ? (rand() - 0.5) * 1.2 : (p.halfWidth + 0.9) * (rand() < 0.5 ? 1 : -1);
      }
      a.bobPhase = rand() * Math.PI * 2;
      return true;
    }
    return false;
  }

  const s = { x: 0, z: 0, dx: 0, dz: 1 };
  let seed = 12345;
  const rand = (): number => {
    // Deterministic-ish and allocation-free; ambience needs variety, not entropy.
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  let clock = 0;

  return {
    update(dt, focusX, focusZ, sources): void {
      prepare(sources);
      clock += dt;

      // Top the population up gradually.
      let budget = SPAWNS_PER_FRAME;
      while (budget > 0 && carAgents.length < MAX_CARS && drivable.length > 0) {
        const a: Agent = { path: null, t: 0, dir: 1, speed: 8, lateral: 1, bobPhase: 0 };
        if (!respawn(a, drivable, focusX, focusZ, true, rand)) break;
        carAgents.push(a);
        budget -= 1;
      }
      while (budget > 0 && walkAgents.length < MAX_PEOPLE && walkable.length > 0) {
        const a: Agent = { path: null, t: 0, dir: 1, speed: 1.3, lateral: 0, bobPhase: 0 };
        if (!respawn(a, walkable, focusX, focusZ, false, rand)) break;
        walkAgents.push(a);
        budget -= 1;
      }

      // Cars.
      for (let i = 0; i < carAgents.length; i += 1) {
        const a = carAgents[i];
        if (!a) continue;
        if (a.path) {
          a.t += a.speed * dt * a.dir;
          if (a.t < 0 || a.t > a.path.length) a.path = null;
        }
        if (a.path) {
          sample(a.path, a.t, s);
          if (Math.hypot(s.x - focusX, s.z - focusZ) > LIVE_R) a.path = null;
        }
        if (!a.path && !respawn(a, drivable, focusX, focusZ, true, rand)) {
          cars.setMatrixAt(i, zero);
          continue;
        }
        if (!a.path) continue;
        sample(a.path, a.t, s);
        // Travel frame: forward flips with dir; keep-left = 90° left of travel.
        const fx = s.dx * a.dir;
        const fz = s.dz * a.dir;
        const ox = fz * a.lateral;
        const oz = -fx * a.lateral;
        dummy.position.set(s.x + ox, 0.33, s.z + oz);
        dummy.rotation.set(0, Math.atan2(-fz, fx) + Math.PI / 2, 0);
        dummy.updateMatrix();
        cars.setMatrixAt(i, dummy.matrix);
        if (i >= cars.count) {
          colour.setHex(CAR_COLOURS[i % CAR_COLOURS.length] ?? 0xd8d2c4);
          cars.setColorAt(i, colour);
        }
      }
      cars.count = carAgents.length;
      cars.instanceMatrix.needsUpdate = true;
      if (cars.instanceColor) cars.instanceColor.needsUpdate = true;

      // People.
      for (let i = 0; i < walkAgents.length; i += 1) {
        const a = walkAgents[i];
        if (!a) continue;
        if (a.path) {
          a.t += a.speed * dt * a.dir;
          if (a.t < 0 || a.t > a.path.length) a.path = null;
        }
        if (a.path) {
          sample(a.path, a.t, s);
          if (Math.hypot(s.x - focusX, s.z - focusZ) > LIVE_R) a.path = null;
        }
        if (!a.path && !respawn(a, walkable, focusX, focusZ, false, rand)) {
          bodies.setMatrixAt(i, zero);
          heads.setMatrixAt(i, zero);
          continue;
        }
        if (!a.path) continue;
        sample(a.path, a.t, s);
        const fx = s.dx * a.dir;
        const fz = s.dz * a.dir;
        const ox = fz * a.lateral;
        const oz = -fx * a.lateral;
        const bob = Math.sin(clock * 7 + a.bobPhase) * 0.03;
        dummy.position.set(s.x + ox, 0.32 + bob, s.z + oz);
        dummy.rotation.set(0, Math.atan2(-fz, fx) + Math.PI / 2, 0);
        dummy.updateMatrix();
        bodies.setMatrixAt(i, dummy.matrix);
        heads.setMatrixAt(i, dummy.matrix);
        if (i >= bodies.count) {
          colour.setHex(SHIRT_COLOURS[i % SHIRT_COLOURS.length] ?? 0x3f6fc9);
          bodies.setColorAt(i, colour);
          colour.setHex(SKIN);
          heads.setColorAt(i, colour);
        }
      }
      bodies.count = walkAgents.length;
      heads.count = walkAgents.length;
      bodies.instanceMatrix.needsUpdate = true;
      heads.instanceMatrix.needsUpdate = true;
      if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
      if (heads.instanceColor) heads.instanceColor.needsUpdate = true;
    },

    dispose(): void {
      for (const m of [cars, bodies, heads]) {
        scene.remove(m);
        m.geometry.dispose();
        m.dispose();
      }
      carMat.dispose();
      peopleMat.dispose();
    },
  };
}
